import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../api';
import './AuditorPortalPage.css';
import { catClass as sharedCatClass } from '../../utils/tsc';

const catClass = (shorthand) => sharedCatClass(shorthand, 'ap');

function barColor(pct) {
  if (pct >= 80) return '#16a34a';
  if (pct >= 50) return '#d97706';
  return '#dc2626';
}

function EviTypeBadge({ type }) {
  const { t } = useTranslation();
  const map = {
    AUTOMATED_TEST: { icon: 'fa-flask',          label: t('AuditorPortalPage.eviTypeAutomatedTest'), cls: 'evi-test' },
    POLICY:         { icon: 'fa-file-signature',  label: t('AuditorPortalPage.eviTypePolicy'),        cls: 'evi-policy' },
    ACCESS_REVIEW:  { icon: 'fa-user-check',      label: t('AuditorPortalPage.eviTypeAccessReview'), cls: 'evi-review' },
  };
  const m = map[type] || map.AUTOMATED_TEST;
  return (
    <span className={`ap-evi-badge ${m.cls}`}>
      <i className={`fas ${m.icon}`} /> {m.label}
    </span>
  );
}

export default function AuditorPortalPage() {
  const { t } = useTranslation();
  const { token }   = useParams();
  const [data,      setData]      = useState(null);
  const [selected,  setSelected]  = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [gone,      setGone]      = useState(false);
  const [notFound,  setNotFound]  = useState(false);
  // R6 access gate
  const [gate,      setGate]      = useState(null);   // { requiresOtp, requireNda, ndaText, emailHint }
  const [otpSent,   setOtpSent]   = useState(false);
  const [code,      setCode]      = useState('');
  const [acceptNda, setAcceptNda] = useState(false);
  const [busy,      setBusy]      = useState(false);
  const [gateErr,   setGateErr]   = useState('');

  const loadPortal = (grant) => {
    setLoading(true);
    const cfg = grant ? { headers: { 'X-Portal-Access': grant } } : {};
    axiosInstance.get(`/auditor-portal/${token}`, cfg)
      .then(r => {
        setData(r.data); setGate(null);
        if (r.data.hubData?.length > 0) setSelected(r.data.hubData[0]);
      })
      .catch(err => {
        const s = err.response?.status;
        if (s === 401 && err.response?.data?.requiresOtp) setGate(err.response.data);
        else if (s === 410 || s === 429) setGone(true);
        else setNotFound(true);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadPortal(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const requestOtp = () => {
    setBusy(true); setGateErr('');
    axiosInstance.post(`/auditor-portal/${token}/request-otp`)
      .then(() => setOtpSent(true))
      .catch(e => setGateErr(e.response?.data?.error || t('AuditorPortalPage.otpSendError', 'Could not send the code. Please try again.')))
      .finally(() => setBusy(false));
  };

  const verifyOtp = () => {
    setBusy(true); setGateErr('');
    axiosInstance.post(`/auditor-portal/${token}/verify-otp`, { code, acceptNda })
      .then(r => loadPortal(r.data.accessToken))
      .catch(e => {
        if (e.response?.data?.requiresNda) {
          setGate(g => ({ ...(g || {}), requireNda: true, ndaText: e.response.data.ndaText }));
          setGateErr(t('AuditorPortalPage.ndaRequired', 'Please accept the confidentiality notice to continue.'));
        } else {
          setGateErr(t('AuditorPortalPage.otpInvalid', 'That code is invalid or has expired.'));
        }
      })
      .finally(() => setBusy(false));
  };

  if (loading) return (
    <div className="ap-page">
      <div className="ap-spinner-wrap"><div className="ap-spinner" /></div>
    </div>
  );

  if (gone) return (
    <div className="ap-page">
      <div className="ap-error-state">
        <i className="fas fa-clock ap-error-icon" />
        <h2>{t('AuditorPortalPage.expiredTitle')}</h2>
        <p>{t('AuditorPortalPage.expiredBody')}</p>
      </div>
    </div>
  );

  // R6 access gate: require an emailed one-time code (and optional NDA) before showing data.
  if (gate && !data) return (
    <div className="ap-page">
      <div className="ap-error-state" style={{ maxWidth: 440 }}>
        <i className="fas fa-lock ap-error-icon" />
        <h2>{t('AuditorPortalPage.gateTitle', 'Verify your access')}</h2>
        <p>{t('AuditorPortalPage.gateBody', 'This audit report is protected. Request a one-time code sent to your email to continue.')}</p>

        {!otpSent ? (
          <button className="gpm-generate" onClick={requestOtp} disabled={busy}
                  style={{ marginTop: 12, padding: '10px 18px', borderRadius: 8 }}>
            {busy ? <><i className="fas fa-spinner fa-spin" /> {t('AuditorPortalPage.sending', 'Sending…')}</>
                  : <>{t('AuditorPortalPage.sendCode', 'Email me a code')}{gate.emailHint ? ` (${gate.emailHint})` : ''}</>}
          </button>
        ) : (
          <div style={{ marginTop: 12, textAlign: 'left' }}>
            <input className="gpm-input" inputMode="numeric" maxLength={6} value={code}
                   onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                   placeholder={t('AuditorPortalPage.codePlaceholder', 'Enter the 6-digit code')}
                   style={{ width: '100%', textAlign: 'center', letterSpacing: 4, fontSize: 18 }} />
            {gate.requireNda && (
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 12, fontSize: 13, color: '#334155' }}>
                <input type="checkbox" checked={acceptNda} onChange={e => setAcceptNda(e.target.checked)} style={{ marginTop: 3 }} />
                <span>{gate.ndaText || t('AuditorPortalPage.ndaDefault', 'I agree to keep this information confidential.')}</span>
              </label>
            )}
            <button className="gpm-generate" onClick={verifyOtp}
                    disabled={busy || code.length < 6 || (gate.requireNda && !acceptNda)}
                    style={{ marginTop: 12, width: '100%', padding: '10px 18px', borderRadius: 8 }}>
              {busy ? <><i className="fas fa-spinner fa-spin" /> {t('AuditorPortalPage.verifying', 'Verifying…')}</>
                    : t('AuditorPortalPage.verify', 'Verify & view')}
            </button>
            <button onClick={requestOtp} disabled={busy}
                    style={{ marginTop: 8, background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: 12 }}>
              {t('AuditorPortalPage.resend', 'Resend code')}
            </button>
          </div>
        )}
        {gateErr && <p style={{ color: '#dc2626', marginTop: 10, fontSize: 13 }}>{gateErr}</p>}
      </div>
    </div>
  );

  if (notFound || !data) return (
    <div className="ap-page">
      <div className="ap-error-state">
        <i className="fas fa-unlink ap-error-icon" />
        <h2>{t('AuditorPortalPage.notFoundTitle')}</h2>
        <p>{t('AuditorPortalPage.notFoundBody')}</p>
      </div>
    </div>
  );

  const categories = data.hubData || [];

  return (
    <div className="ap-page">
      {/* Header */}
      <div className="ap-header">
        <div className="ap-header-inner">
          <div className="ap-brand">
            <div className="ap-brand-logo"><i className="fas fa-shield-alt" /></div>
            <div>
              <div className="ap-brand-name">{data.orgName}</div>
              <div className="ap-brand-sub">{t('AuditorPortalPage.brandSub')}</div>
            </div>
          </div>
          {data.cpaName && (
            <div className="ap-prepared-for">
              <div className="ap-pf-label">{t('AuditorPortalPage.preparedFor')}</div>
              <div className="ap-pf-name">{data.cpaName}</div>
              {data.cpaEmail && <div className="ap-pf-email">{data.cpaEmail}</div>}
            </div>
          )}
        </div>
        <div className="ap-meta-bar">
          <span><i className="fas fa-eye" /> {t('AuditorPortalPage.viewCount', { count: data.viewCount })}</span>
          <span><i className="fas fa-calendar-alt" /> {t('AuditorPortalPage.expires', { date: new Date(data.expiresAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) })}</span>
          <span><i className="fas fa-lock" /> {t('AuditorPortalPage.readOnlyNoLogin')}</span>
        </div>
      </div>

      {/* Content */}
      <div className="ap-content">
        {categories.length === 0 ? (
          <div className="ap-empty">
            <i className="fas fa-database" />
            <p>{t('AuditorPortalPage.emptyState')}</p>
          </div>
        ) : (
          <div className="ap-layout">
            {/* Control tree */}
            <div className="ap-tree">
              <div className="ap-tree-title">{t('AuditorPortalPage.tscControlCategories')}</div>
              {categories.map(cat => (
                <div key={cat.shorthand}
                     className={`ap-tree-item ${selected?.shorthand === cat.shorthand ? 'active' : ''}`}
                     onClick={() => setSelected(cat)}>
                  <span className={`ap-cat-chip ${catClass(cat.shorthand)}`}>{cat.shorthand}</span>
                  {cat.inScope === false && (
                    <span className="ap-oos-badge">{t('AuditorPortal.notInScope')}</span>
                  )}
                  <div className="ap-tree-info">
                    <div className="ap-tree-name" title={cat.name}>{cat.name}</div>
                    <div className="ap-tree-pct">{t('AuditorPortalPage.coverageTests', { coverage: cat.coverage, total: cat.total })}</div>
                    <div className="ap-bar-wrap">
                      <div className="ap-bar" style={{ width: `${cat.coverage}%`, background: barColor(cat.coverage) }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Evidence panel */}
            {selected && (
              <div className="ap-panel">
                <div className="ap-panel-head">
                  <span className={`ap-cat-chip ${catClass(selected.shorthand)}`}>{selected.shorthand}</span>
                  <div>
                    <div className="ap-panel-title">{selected.name}</div>
                    <div className="ap-panel-sub">{t('AuditorPortalPage.panelSub')}</div>
                  </div>
                </div>

                <div className="ap-stats-row">
                  {[
                    { label: t('AuditorPortalPage.statCoverage'), val: `${selected.coverage}%`, color: barColor(selected.coverage) },
                    { label: t('AuditorPortalPage.statPassing'),  val: selected.passed,          color: '#16a34a' },
                    { label: t('AuditorPortalPage.statFailing'),  val: selected.failed,          color: '#dc2626' },
                    { label: t('AuditorPortalPage.statNotRun'),  val: selected.notRun,          color: '#94a3b8' },
                  ].map(s => (
                    <div key={s.label} className="ap-stat-box">
                      <div className="ap-stat-val" style={{ color: s.color }}>{s.val}</div>
                      <div className="ap-stat-lbl">{s.label}</div>
                    </div>
                  ))}
                </div>

                <div className="ap-evidence-section">
                  <div className="ap-evidence-heading">{t('AuditorPortalPage.evidenceItems')}</div>
                  {(selected.evidence || []).map((ev, i) => (
                    <div key={i} className="ap-evidence-item">
                      <EviTypeBadge type={ev.type} />
                      <div className="ap-evi-label">{ev.label}</div>
                      <div className="ap-evi-detail">{ev.detail}</div>
                      <span className={`ap-evi-status ${ev.status === 'COMPLETE' ? 'evi-ok' : ev.status === 'FAILING' ? 'evi-fail' : 'evi-partial'}`}>
                        {ev.status === 'COMPLETE' ? t('AuditorPortalPage.statusComplete') : ev.status === 'FAILING' ? t('AuditorPortalPage.statusFailing') : t('AuditorPortalPage.statusInProgress')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {data.aiGovernance && <AiGovernanceAuditorSection t={t} ai={data.aiGovernance} />}

      <div className="ap-footer">
        <span>{t('AuditorPortalPage.poweredByPrefix')}<strong>CATAAM</strong>{t('AuditorPortalPage.poweredBySuffix')}</span>
        <span>·</span>
        <span>{t('AuditorPortalPage.generatedBy', { name: data.generatedBy })}</span>
      </div>
    </div>
  );
}

/** Read-only AI Management System (ISO 42001 / NIST AI RMF) view for the auditor portal (PRD-74 E1). */
function AiGovernanceAuditorSection({ t, ai }) {
  const scope = ai.scopeStatement || {};
  const soas = ai.statementsOfApplicability || [];
  const risks = ai.riskRegister || [];
  const assets = ai.assetInventory || [];
  const cell = { border: '1px solid #e2e8f0', padding: '0.4rem 0.6rem', fontSize: '0.8rem', textAlign: 'left' };
  const th = { ...cell, background: '#f8fafc', fontWeight: 600 };
  return (
    <div className="ap-content" style={{ marginTop: '1.5rem' }}>
      <h2 style={{ fontSize: '1.1rem', color: '#1e293b' }}>
        <i className="fas fa-brain" style={{ marginRight: '0.5rem' }} />
        {t('AuditorPortalPage.aiGovTitle', 'AI Management System')}
      </h2>
      {scope.context && <p style={{ fontSize: '0.85rem', color: '#475569' }}>{scope.context}</p>}

      <h3 style={{ fontSize: '0.95rem', color: '#334155', marginTop: '1rem' }}>{t('AuditorPortalPage.aiGovReadiness', 'Framework readiness')}</h3>
      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
        <thead><tr><th style={th}>{t('AuditorPortalPage.aiGovFramework', 'Framework')}</th><th style={th}>{t('AuditorPortalPage.aiGovReadinessCol', 'Readiness')}</th><th style={th}>{t('AuditorPortalPage.aiGovImplemented', 'Implemented')}</th><th style={th}>{t('AuditorPortalPage.aiGovExcluded', 'Excluded')}</th></tr></thead>
        <tbody>
          {soas.map((s) => (
            <tr key={s.frameworkId}>
              <td style={cell}>{s.frameworkName}</td>
              <td style={cell}><strong style={{ color: s.readinessPct >= 80 ? '#16a34a' : s.readinessPct >= 50 ? '#ca8a04' : '#dc2626' }}>{s.readinessPct}%</strong></td>
              <td style={cell}>{s.implementedCount} / {s.applicableCount}</td>
              <td style={cell}>{s.excludedCount}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3 style={{ fontSize: '0.95rem', color: '#334155', marginTop: '1rem' }}>{t('AuditorPortalPage.aiGovAssets', 'AI systems in scope')} ({assets.length})</h3>
      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
        <thead><tr><th style={th}>{t('AuditorPortalPage.aiGovName', 'Name')}</th><th style={th}>{t('AuditorPortalPage.aiGovType', 'Type')}</th><th style={th}>{t('AuditorPortalPage.aiGovRiskTier', 'Risk tier')}</th><th style={th}>{t('AuditorPortalPage.aiGovLifecycle', 'Lifecycle')}</th></tr></thead>
        <tbody>
          {assets.map((a, i) => (
            <tr key={i}><td style={cell}>{a.name}</td><td style={cell}>{a.type}</td><td style={cell}>{a.riskTier || '—'}</td><td style={cell}>{a.lifecycleStage || '—'}</td></tr>
          ))}
        </tbody>
      </table>

      <p style={{ fontSize: '0.82rem', color: '#475569', marginTop: '0.75rem' }}>
        {t('AuditorPortalPage.aiGovRisks', 'Open AI risks')}: <strong>{risks.length}</strong>
      </p>
    </div>
  );
}
